import { create } from 'zustand'

/**
 * Drag a ticker from one place in the app to another (Chris, 2026-10-01):
 * scanner / watchlist / news tickers drop onto a watchlist (adds it) or the
 * Edge AI chat (inserts `$TICKER`).
 *
 * Native HTML5 drag, not react-dnd: the source is an AG Grid cell, and the
 * app's react-dnd HTML5 backend cancels any native drag that carries no
 * standard type, so every ticker drag also sets `text/plain` (`$AAPL`). The
 * custom type is what drop targets key off, so plain text dragged from
 * elsewhere never triggers them.
 */
export const TICKER_MIME = 'application/x-edge-ticker'

interface TickerDragState {
  /** The ticker being dragged right now, or null. Drives target outlines. */
  ticker: string | null
  setTicker: (ticker: string | null) => void
}

export const useTickerDragStore = create<TickerDragState>((set) => ({
  ticker: null,
  setTicker: (ticker) => set({ ticker }),
}))

const normalize = (ticker: string) => ticker.trim().toUpperCase()

/** dragover events a ticker drop target accepted (see markTickerTarget). */
const acceptedEvents = new WeakSet<Event>()

/** A drop target calls this from onDragOver so the global guard leaves it alone. */
export function markTickerTarget(e: Event): void {
  acceptedEvents.add(e)
}

// Outside a drop target the cursor shows "no drop", instead of the copy
// cursor the react-dnd backend's blanket preventDefault would otherwise leave.
// Runs on window (bubble), after any target's own handler has marked the event.
function onWindowDragOver(e: DragEvent) {
  if (!acceptedEvents.has(e) && e.dataTransfer && isTickerDrag(e.dataTransfer)) {
    e.dataTransfer.dropEffect = 'none'
  }
}

function onWindowDragDone() {
  endTickerDrag()
}

/** A small `$AAPL` chip as the drag image (the default is a ghost of the cell). */
function setChipDragImage(dt: DataTransfer, ticker: string) {
  if (typeof dt.setDragImage !== 'function') return
  const chip = document.createElement('div')
  chip.textContent = `$${ticker}`
  Object.assign(chip.style, {
    position: 'fixed',
    top: '-1000px',
    left: '-1000px',
    padding: '2px 8px',
    borderRadius: '6px',
    font: '600 12px Roboto, sans-serif',
    background: 'hsl(var(--primary))',
    color: 'hsl(var(--primary-foreground))',
  })
  document.body.appendChild(chip)
  dt.setDragImage(chip, 12, 10)
  // The browser snapshots the image synchronously; the node can go next tick.
  setTimeout(() => chip.remove(), 0)
}

/** Call from a drag source's onDragStart. */
export function startTickerDrag(dt: DataTransfer, rawTicker: string): void {
  const ticker = normalize(rawTicker)
  dt.setData(TICKER_MIME, ticker)
  dt.setData('text/plain', `$${ticker}`)
  dt.effectAllowed = 'copy'
  setChipDragImage(dt, ticker)
  useTickerDragStore.getState().setTicker(ticker)
  window.addEventListener('dragover', onWindowDragOver)
  window.addEventListener('dragend', onWindowDragDone, true)
  window.addEventListener('drop', onWindowDragDone)
}

export function endTickerDrag(): void {
  window.removeEventListener('dragover', onWindowDragOver)
  window.removeEventListener('dragend', onWindowDragDone, true)
  window.removeEventListener('drop', onWindowDragDone)
  if (useTickerDragStore.getState().ticker !== null) {
    useTickerDragStore.getState().setTicker(null)
  }
}

/** True while dragging a ticker (types are readable during dragover; data is not). */
export function isTickerDrag(dt: DataTransfer | null): boolean {
  return !!dt && Array.from(dt.types ?? []).includes(TICKER_MIME)
}

/** The dropped ticker, readable only in the drop event. */
export function readTickerDrop(dt: DataTransfer | null): string | null {
  const raw = dt?.getData(TICKER_MIME)
  return raw ? normalize(raw) : null
}
